import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, inp, label, card, btnPrimary } from "./AdminShell";
import { useAuth } from "@/lib/auth";
import { Plus, Trash2, X, Save, Edit, ToggleLeft, ToggleRight, KeyRound, Copy } from "lucide-react";

/* ============ BUSINESS SETTINGS ============ */
export function AdminBusinessSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-neutral-500">Memuat...</div>;
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Pengaturan berhasil disimpan."); }
    catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <PageHeader title="Business Settings" subtitle="Informasi perusahaan, kontak, dan sosial media" />
      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl">
        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-4">Identitas</h2>
          <div className="space-y-3">
            <div><label className={label}>Nama Perusahaan</label><input value={s.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className={inp} data-testid="set-company_name" /></div>
            <div><label className={label}>Nama Legal (opsional)</label><input value={s.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} className={inp} placeholder="PT ..." /></div>
            <div><label className={label}>Alamat</label><textarea rows={3} value={s.address || ""} onChange={(e) => set("address", e.target.value)} className={inp} data-testid="set-address" /></div>
            <div><label className={label}>Jam Operasional</label><textarea rows={3} value={s.business_hours || ""} onChange={(e) => set("business_hours", e.target.value)} className={inp} placeholder="Senin - Jumat: 08.00 - 17.00 WIB&#10;Sabtu: 08.00 - 14.00 WIB" /></div>
          </div>
        </div>

        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-4">Kontak</h2>
          <div className="space-y-3">
            <div><label className={label}>WhatsApp (628...)</label><input value={s.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className={inp} placeholder="6281234567890" data-testid="set-whatsapp" /></div>
            <div><label className={label}>Telepon</label><input value={s.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inp} data-testid="set-phone" /></div>
            <div><label className={label}>Email</label><input type="email" value={s.email || ""} onChange={(e) => set("email", e.target.value)} className={inp} data-testid="set-email" /></div>
            <div><label className={label}>Google Maps Embed URL</label><textarea rows={2} value={s.google_maps_embed || ""} onChange={(e) => set("google_maps_embed", e.target.value)} className={inp} placeholder="URL embed atau iframe src" /></div>
          </div>
        </div>

        <div className={`${card} p-6 lg:col-span-2`}>
          <h2 className="font-display font-bold text-neutral-900 mb-4">Social Media</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className={label}>Instagram</label><input value={s.instagram || ""} onChange={(e) => set("instagram", e.target.value)} className={inp} placeholder="https://instagram.com/..." /></div>
            <div><label className={label}>Facebook</label><input value={s.facebook || ""} onChange={(e) => set("facebook", e.target.value)} className={inp} placeholder="https://facebook.com/..." /></div>
            <div><label className={label}>TikTok</label><input value={s.tiktok || ""} onChange={(e) => set("tiktok", e.target.value)} className={inp} placeholder="https://tiktok.com/@..." /></div>
            <div><label className={label}>YouTube</label><input value={s.youtube || ""} onChange={(e) => set("youtube", e.target.value)} className={inp} placeholder="https://youtube.com/..." /></div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <Button type="submit" disabled={saving} className={`${btnPrimary} h-11 px-6`} data-testid="settings-save">
            <Save size={14} /> {saving ? "Menyimpan..." : "Simpan Pengaturan"}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* ============ SEO SETTINGS ============ */
export function AdminSEOSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-neutral-500">Memuat...</div>;
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/admin/settings", s); toast.success("Pengaturan SEO tersimpan."); }
    catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <PageHeader title="SEO & Analytics" subtitle="Default metadata, verifikasi search engine, dan tracking" />
      <form onSubmit={save} className="grid grid-cols-1 gap-4 max-w-3xl">
        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-4">SEO Default</h2>
          <div className="space-y-3">
            <div><label className={label}>Default Title</label><input value={s.seo_default_title || ""} onChange={(e) => set("seo_default_title", e.target.value)} className={inp} data-testid="set-seo_title" /></div>
            <div><label className={label}>Default Meta Description</label><textarea rows={3} value={s.seo_default_description || ""} onChange={(e) => set("seo_default_description", e.target.value)} className={inp} data-testid="set-seo_desc" /></div>
            <div><label className={label}>Default OG Image URL</label><input value={s.seo_og_image || ""} onChange={(e) => set("seo_og_image", e.target.value)} className={inp} placeholder="URL gambar untuk social sharing" /></div>
          </div>
        </div>

        <div className={`${card} p-6`}>
          <h2 className="font-display font-bold text-neutral-900 mb-4">Analytics & Tracking</h2>
          <p className="text-xs text-neutral-500 mb-4">Script tracking hanya akan dimuat jika ID diisi. Kosongkan untuk menonaktifkan.</p>
          <div className="space-y-3">
            <div><label className={label}>Google Search Console Verification</label><input value={s.gsc_verification || ""} onChange={(e) => set("gsc_verification", e.target.value)} className={inp} placeholder="content value dari meta tag" /></div>
            <div><label className={label}>Google Analytics ID</label><input value={s.ga_id || ""} onChange={(e) => set("ga_id", e.target.value)} className={inp} placeholder="G-XXXXXXXXXX" /></div>
            <div><label className={label}>Meta (Facebook) Pixel ID</label><input value={s.meta_pixel_id || ""} onChange={(e) => set("meta_pixel_id", e.target.value)} className={inp} placeholder="123456789012345" /></div>
            <div><label className={label}>Google Ads Conversion ID</label><input value={s.google_ads_id || ""} onChange={(e) => set("google_ads_id", e.target.value)} className={inp} placeholder="AW-XXXXXXXXX" /></div>
          </div>
        </div>

        <div>
          <Button type="submit" disabled={saving} className={`${btnPrimary} h-11 px-6`} data-testid="seo-save"><Save size={14} /> {saving ? "Menyimpan..." : "Simpan Pengaturan SEO"}</Button>
        </div>
      </form>
    </div>
  );
}

/* ============ USERS ============ */
export function AdminUsers() {
  const { user: me, refresh: refreshMe } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tempPasswordDialog, setTempPasswordDialog] = useState(null);

  const refresh = () => api.get("/admin/users").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { refresh(); }, []);

  const activeAdminCount = list.filter((u) => u.role === "admin" && u.status !== "Nonaktif").length;

  const addUser = async (form) => {
    try {
      const { data } = await api.post("/admin/users", form);
      if (data.temp_password) {
        setTempPasswordDialog({ email: data.email, password: data.temp_password });
      } else {
        toast.success("Admin baru berhasil ditambahkan.");
      }
      setShowAdd(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menambah admin"); }
  };

  const patchUser = async (id, updates, successMsg) => {
    try {
      await api.patch(`/admin/users/${id}`, updates);
      toast.success(successMsg || "Perubahan tersimpan.");
      setEditing(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan perubahan"); }
  };

  const toggleStatus = async (u) => {
    const next = u.status === "Nonaktif" ? "Aktif" : "Nonaktif";
    if (next === "Nonaktif") {
      const msg = `Nonaktifkan akun ${u.email}?\n\nAkun tidak akan bisa login sampai diaktifkan kembali.`;
      if (!window.confirm(msg)) return;
    }
    await patchUser(u.id, { status: next }, next === "Nonaktif" ? "Akun dinonaktifkan." : "Akun diaktifkan kembali.");
  };

  const del = async (u) => {
    const msg = `Hapus pengguna ${u.name || u.email}?\n\nAksi ini akan menghapus akses ke admin panel secara permanen. Pertimbangkan untuk menonaktifkan akun sebagai gantinya.`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Pengguna dihapus."); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  const isSelf = (u) => me?.id && u.id === me.id;
  const canDelete = (u) => !isSelf(u) && !(u.role === "admin" && u.status !== "Nonaktif" && activeAdminCount <= 1);
  const canDisable = (u) => !isSelf(u) && !(u.role === "admin" && u.status !== "Nonaktif" && activeAdminCount <= 1 && u.status !== "Nonaktif");

  const roleBadge = (r) => r === "editor"
    ? "bg-blue-50 text-blue-700"
    : "bg-[#FF9418]/10 text-[#FF9418]";
  const statusBadge = (s) => s === "Nonaktif"
    ? "bg-neutral-100 text-neutral-500"
    : "bg-green-50 text-green-700";
  const roleLabel = (r) => r === "editor" ? "Editor" : "Administrator";
  const fmtLastLogin = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  return (
    <div>
      <PageHeader
        title="Pengguna"
        subtitle="Kelola akun administrator & editor yang mengakses panel"
        actions={<Button onClick={() => setShowAdd(true)} className={`${btnPrimary} h-10 px-4`} data-testid="new-user-btn"><Plus size={14} /> Tambah Admin</Button>}
      />

      {/* Akun Saya — self password change */}
      <AccountSecurityCard me={me} onChanged={refreshMe} />

      <div className={`${card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-[11px] font-mono uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3">Nama</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Role</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Status</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Terakhir Login</th>
                <th className="text-right px-4 py-3">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {loading ? <tr><td colSpan="6" className="text-center py-8 text-neutral-500">Memuat...</td></tr> :
                list.map((u) => (
                  <tr key={u.id} className={`hover:bg-neutral-50 ${u.status === "Nonaktif" ? "opacity-70" : ""}`} data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-neutral-900">{u.name || "—"}</span>
                        {isSelf(u) && <span className="bg-[#FF9418] text-white text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded" data-testid="badge-anda">Anda</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{u.email}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase ${roleBadge(u.role)}`}>{roleLabel(u.role)}</span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase ${statusBadge(u.status)}`}>{u.status || "Aktif"}</span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-neutral-500 font-mono text-xs whitespace-nowrap">{fmtLastLogin(u.last_login)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(u)} className="text-neutral-500 hover:text-[#FF9418] p-1.5" title="Edit" data-testid={`edit-user-${u.email}`}><Edit size={14} /></button>
                      {canDisable(u) && (
                        <button onClick={() => toggleStatus(u)} className="text-neutral-500 hover:text-[#FF9418] p-1.5"
                          title={u.status === "Nonaktif" ? "Aktifkan" : "Nonaktifkan"}
                          data-testid={`toggle-user-${u.email}`}>
                          {u.status === "Nonaktif" ? <ToggleRight size={16} className="text-green-600" /> : <ToggleLeft size={16} />}
                        </button>
                      )}
                      {canDelete(u) && (
                        <button onClick={() => del(u)} className="text-neutral-500 hover:text-red-600 p-1.5" title="Hapus" data-testid={`del-user-${u.email}`}><Trash2 size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onSubmit={addUser} />}
      {editing && <EditUserModal user={editing} isSelf={isSelf(editing)} activeAdminCount={activeAdminCount} onClose={() => setEditing(null)} onSubmit={(updates) => patchUser(editing.id, updates)} />}
      {tempPasswordDialog && <TempPasswordDialog data={tempPasswordDialog} onClose={() => setTempPasswordDialog(null)} />}
    </div>
  );
}

/* -- Change own password card -- */
function AccountSecurityCard({ me, onChanged }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast.error("Password baru minimal 8 karakter.");
    if (form.new_password !== form.confirm) return toast.error("Konfirmasi password tidak cocok.");
    setSaving(true);
    try {
      await api.patch("/auth/password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password berhasil diubah.");
      setForm({ current_password: "", new_password: "", confirm: "" });
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengubah password"); }
    finally { setSaving(false); }
  };
  return (
    <div className={`${card} p-5 mb-4 max-w-2xl`}>
      <div className="mb-3">
        <h2 className="font-display font-bold text-neutral-900">Akun & Keamanan</h2>
        <p className="text-xs text-neutral-500">Ubah password akun Anda ({me?.email}).</p>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><label className={label}>Password Saat Ini</label><input required type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className={inp} data-testid="pw-current" autoComplete="current-password" /></div>
        <div><label className={label}>Password Baru (min 8)</label><input required type="password" minLength={8} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className={inp} data-testid="pw-new" autoComplete="new-password" /></div>
        <div><label className={label}>Konfirmasi Password Baru</label><input required type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inp} data-testid="pw-confirm" autoComplete="new-password" /></div>
        <div className="md:col-span-3">
          <Button type="submit" disabled={saving} className={`${btnPrimary} h-10 px-5`} data-testid="pw-submit"><KeyRound size={14} /> {saving ? "Menyimpan..." : "Ubah Password"}</Button>
        </div>
      </form>
    </div>
  );
}

/* -- Add user modal -- */
function AddUserModal({ onClose, onSubmit }) {
  const [form, setForm] = useState({ name: "", email: "", role: "admin", password: "" });
  const [generateTemp, setGenerateTemp] = useState(true);
  const submit = (e) => {
    e.preventDefault();
    if (!generateTemp && form.password.length < 8) return toast.error("Password minimal 8 karakter.");
    const payload = { ...form };
    if (generateTemp) payload.password = "";
    onSubmit(payload);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg text-neutral-900">Tambah Admin</h2>
          <button onClick={onClose} className="text-neutral-500"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div><label className={label}>Nama</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="user-name" /></div>
          <div><label className={label}>Email *</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} data-testid="user-email" /></div>
          <div>
            <label className={label}>Role *</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp} data-testid="user-role">
              <option value="admin">Administrator — akses penuh</option>
              <option value="editor">Editor — Produk / Media / Konten / Permintaan</option>
            </select>
          </div>
          <label className="flex items-start gap-2 cursor-pointer bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2">
            <input type="checkbox" checked={generateTemp} onChange={(e) => setGenerateTemp(e.target.checked)} className="accent-[#FF9418] mt-1" data-testid="user-generate-temp" />
            <span className="text-xs text-neutral-700">Generate password sementara secara otomatis. Password ditampilkan sekali & user diminta mengubahnya saat login pertama.</span>
          </label>
          {!generateTemp && (
            <div><label className={label}>Password Awal (min 8 karakter)</label><input type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} data-testid="user-password" /></div>
          )}
          <div className="flex gap-2 pt-2">
            <Button type="submit" className={`${btnPrimary} h-10 px-5 flex-1`} data-testid="user-save">Tambah</Button>
            <Button type="button" onClick={onClose} variant="outline" className="h-10 px-5 border-neutral-300">Batal</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -- Edit user modal -- */
function EditUserModal({ user, isSelf, activeAdminCount, onClose, onSubmit }) {
  const [form, setForm] = useState({ name: user.name || "", role: user.role || "admin", status: user.status || "Aktif" });
  const submit = (e) => { e.preventDefault(); onSubmit(form); };
  const lastActiveAdmin = user.role === "admin" && user.status !== "Nonaktif" && activeAdminCount <= 1;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg text-neutral-900">Edit Pengguna</h2>
          <button onClick={onClose} className="text-neutral-500"><X size={18} /></button>
        </div>
        <div className="text-xs text-neutral-500 mb-3 font-mono">{user.email}</div>
        <form onSubmit={submit} className="space-y-3">
          <div><label className={label}>Nama</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="edit-user-name" /></div>
          <div>
            <label className={label}>Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp} disabled={isSelf || lastActiveAdmin} data-testid="edit-user-role">
              <option value="admin">Administrator</option>
              <option value="editor">Editor</option>
            </select>
            {isSelf && <p className="text-[11px] text-neutral-500 mt-1">Tidak dapat mengubah role akun sendiri.</p>}
            {!isSelf && lastActiveAdmin && <p className="text-[11px] text-orange-600 mt-1">Ini administrator aktif terakhir — tidak dapat diubah menjadi Editor.</p>}
          </div>
          <div>
            <label className={label}>Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inp} disabled={isSelf || lastActiveAdmin} data-testid="edit-user-status">
              <option value="Aktif">Aktif</option>
              <option value="Nonaktif">Nonaktif</option>
            </select>
            {isSelf && <p className="text-[11px] text-neutral-500 mt-1">Tidak dapat menonaktifkan akun sendiri.</p>}
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" className={`${btnPrimary} h-10 px-5 flex-1`} data-testid="edit-user-save">Simpan</Button>
            <Button type="button" onClick={onClose} variant="outline" className="h-10 px-5 border-neutral-300">Batal</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* -- One-time temp password reveal dialog -- */
function TempPasswordDialog({ data, onClose }) {
  const copy = () => { navigator.clipboard.writeText(data.password); toast.success("Password disalin."); };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display font-bold text-lg text-neutral-900 mb-1">Admin baru dibuat</h2>
        <p className="text-sm text-neutral-600 mb-4">Berikan password sementara ini ke <b>{data.email}</b>. Password hanya ditampilkan sekali, dan mereka akan diminta menggantinya saat login pertama.</p>
        <div className="bg-neutral-100 border border-neutral-200 rounded-md p-3 font-mono text-lg tracking-wider break-all mb-4 text-center" data-testid="temp-password-value">{data.password}</div>
        <div className="flex gap-2">
          <Button onClick={copy} className={`${btnPrimary} h-10 px-5 flex-1`} data-testid="temp-password-copy"><Copy size={14} /> Salin Password</Button>
          <Button onClick={onClose} variant="outline" className="h-10 px-5 border-neutral-300">Tutup</Button>
        </div>
      </div>
    </div>
  );
}
